import { Deserializer } from "jsonapi-serializer";
import { ROLES } from "../constants/preset-constants";
import { ReportTypes, getMusterReportStatus } from "../constants/report-types";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { ACCESS_TYPES } from "../constants/access-constants";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Translator } from "../contexts/translator";
import { AbsenteeismReport, AbsenteeismReportWrapper } from "../models/AbsenteeismReport";
import { AccessGroupReport, AccessGroupReportWrapper } from "../models/AccessGroupReport";
import { AuditReport, AuditReportWrapper } from "../models/AuditReport";
import AuditReportColumn from "../models/ColumnDefinitions/AuditReportColumn";
import { AreaReport, AreaReportWrapper } from "../models/AreaReport";
import { ExpiredCredentialReport, ExpiredCredentialWrapper } from "../models/ExpiredCredentialReport";
import { Filter } from "../models/Filter";
import { FirstInLastOutReport } from "../models/FirstInLastOutReport";
import { genericError } from "../models/GenericError";
import { HardwareInstallationReport, HardwareReportWrapper } from "../models/HardwareInstallationReport";
import { HoursWorkedReport, HoursWorkedReportWrapper } from "../models/HoursWorkedReport";
import { ListQuery } from "../models/ListQuery";
import { MusteringReport, MusteringReportReportWrapper } from "../models/MusteringReport";
import { MusteringReportTotals } from "../models/MusteringReportTotals";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { PersonImage } from "../models/Person";
import { ToastFunction } from "../models/ReactToastifyType";
import { Report, ReportWrapper } from "../models/Report";
import { StillOnSite, StillOnSiteWrapper } from "../models/StillOnSite";
import { TnaTransactionReport, TnaTransactionReportWrapper } from "../models/TnaTransactionReport";
import { TableRowGroup } from "../models/table/TableRowGroup";
import { TransactionReport, TransactionReportWrapper } from "../models/TransactionReport";
import { VingcardTransactionReport, VingcardTransactionReportWrapper } from "../models/VingcardTransactionReport";
import { ZoneOccupancy, ZoneOccupancykWrapper } from "../models/ZoneOccupancy";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { buildFirstInLastOutWeekDays, convertDatesToWeekDays, getCurrentWeekDates, getDatesBetween, getDayName, getTodayDate } from "../utilities/DateUtils";
import { showTagCode } from "../utilities/Permissions";
import { getReportAttributes, formatISODateTime, formatDateOnly, buildAuditSessionParentRow, buildAuditEntityGroupRow, getAuditEntityGroupKey, buildAuditSessionChildRow, getAuditSessionDetails, buildAuditSessionChildRows, buildHoursWorkedParentRow, buildHoursWorkedChildRow } from "../utilities/reportUtil";
import { APIConnector } from "./api/APIConnector";
import { getConstraintViolation, processServerError } from "./utilities/ServiceError";
import { getReportStatus } from "./utilities/StatusBuilder";
import { convertToDuration, convertToTime, getFormattedDate, getFormattedDateTime, getFormattedTime, getFormattedTimeFromNum, getTime, getUnFormattedDate } from "./utilities/dateFormatting";
import { createPagination } from "./utilities/paginationBuilder";
import { appendCustomReportFilters, createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { CardsNotUsedReport, CardsNotUsedReportWrapper } from "../models/CardsNotUsedReport";
import { DEFAULT_TIMEOUT_SYSNAME, FILTER_INACTIVE_DAYS_90, INACTIVE_DAYS_BY_SYSNAME, TIMEOUT_BY_SYSNAME } from "../constants/element-type";
import { DoorAccessReport, DoorAccessReportWrapper } from "@/models/DoorAccessReport";
import { SequentialEventReport, SequentialEventReportWrapper } from "../models/SequentialEventReport";
import { PersonAccessReport, PersonAccessReportWrapper } from "@/models/PersonAccessReport";
import { OperationalComplianceReport, OperationalComplianceReportWrapper } from "@/models/OperationComplianceReport";
import {GeneralAccessReportWrapper} from "@/models/GeneralAcessReport.ts";

const apiConnector = new APIConnector();

const buildTransaction = (transaction, profileId, translate: Translator): TransactionReport => {
    return {
        id: transaction["id"],
        name: transaction["master-by-tr-master-id"]
            ? transaction["master-by-tr-master-id"]["mst-displayname"]
            : transaction["tr-master-mst-displayname"],
        dateTime: transaction["tr-datetimelocal"]
            .replace("T", " ")
            .replace(/\.\d+$/, "")
            .replaceAll("-", "/"),
        eventType: transaction["event-type"]["trn-name"],
        transactionType: transaction["transack-type"]["trn-name"],
        masterController: transaction["tr-ctrl-name"],
        reader:
            transaction["tr-ctrl-name"] === transaction["tr-dev-name"]
                ? ""
                : transaction["tr-dev-name"],
        ossDevice: transaction["tr-ossd-name"],
        building: transaction["tr-building-name"],
        floor: transaction["tr-floor-name"],
        zone: transaction["tr-zone-name"],
        site: transaction["tr-site-name"],
        tagCode: showTagCode(profileId, transaction["tr-tagcode"]),
        fixedAddress: transaction["tr-dev-fixedaddr"],
        logicalAddress: transaction["tr-sla"],
        serialAddress: transaction["tr-dev-serial"],
        company: transaction["tr-comp-name"],
        door: transaction["tr-loc-name"],
        department: transaction["tr-dept-name"],
        firstName: transaction["master-by-tr-master-id"]
            ? transaction["master-by-tr-master-id"]["mst-firstname"]
            : "",
        gender: transaction["master-by-tr-master-id"]
            ? getGenderLabel(transaction["master-by-tr-master-id"]["mst-gender"], translate)
            : "",
        lastName: transaction["master-by-tr-master-id"]
            ? transaction["master-by-tr-master-id"]["mst-lastname"]
            : "",
        middleName: transaction["master-by-tr-master-id"]
            ? transaction["master-by-tr-master-id"]["mst-middlename"]
            : "",
        title: transaction["master-by-tr-master-id"]
            ? transaction["master-by-tr-master-id"]["mst-title"]
            : "",
        category: transaction["master-by-tr-master-id"]?.["profile"]
            ? transaction["master-by-tr-master-id"]["profile"]["trn-name"]
            : "",
        reasonCode: transaction["tr-reason-code-name"],
        deviceType: transaction["unit-type"]["trn-name"],
        direction: transaction["tr-direction"] === 0 ? "Entry" : "Exit",
        access: transaction["master-by-tr-master-id"]?.["master-type"]
            ? transaction["master-by-tr-master-id"]["master-type"]["trn-name"]
            : "",
        dateOnly: transaction["date-only"]
    };
};

export const getTransactionReport = async (profileId: number, translate: Translator, listParameters?: ListQuery): Promise<TransactionReportWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm, 'trDatetimelocal');
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        let includes = "include=eventType,masterByTrMasterId.profile,";
        includes += "masterByTrMasterId.masterType,transackType,company,department,unitType,location,ossDevice";
        let transactionFields = "fields[transack]=masterByTrMasterId,trDatetimelocal,";
        transactionFields += "eventType,transackType,trCtrlName,trDevName,trBuildingName,trFloorName,";
        transactionFields += "trZoneName,trSiteName,trTagcode,trDevFixedaddr,trSla,trDevSerial,"
        transactionFields += "trOssdDeviceId,trOssdName,"
        transactionFields += "trCompName,trLocName,trDeptName,trReasoncode,unitType,trDirection,dateOnly,trReasonCodeName,trMasterMstDisplayname";
        const profileFields = "fields[profile]=trnName";
        const eventTypeFields = "fields[eventType]=trnName";
        const transactionTypeFields = "fields[transackType]=trnName";
        const unitTypeFields = "fields[unitType]=trnName";
        const masterFields = "fields[master]=masterType,mstDisplayname,mstFirstname,mstGender,mstIdnumber,mstLastname,mstMiddlename,mstTitle,mstType,profile";
        const locationFields = "fields[location]=locName";
        const masterTypeFields = "fields[masterType]=trnName";
        const sortOrder = "sort=-trDatetimelocal";
        const url = `transack?${includes}&${transactionFields}&${eventTypeFields}&${transactionTypeFields}&${unitTypeFields}&${masterFields}&${masterTypeFields}&${locationFields}&${profileFields}${partialURL}&${sortOrder}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const transactions: TransactionReport[] = data.map((data) => buildTransaction(data, profileId, translate));

                return resolve({ rows: transactions, pagination });
            }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getUserTransactionReport = async (profileId: number, masterId: number, translate: Translator, listParameters?: ListQuery): Promise<TransactionReportWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm, 'trDatetimelocal');
        const withAdditionalFilter = `${filter ? filter.replace('filter=', `filter=masterByTrMasterId.id==${masterId};`) : `&filter=masterByTrMasterId.id==${masterId}`}`;
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, withAdditionalFilter);
        const includes = "include=eventType,masterByTrMasterId.profile,masterByTrMasterId.masterType,transackType,company,department,unitType,location,ossDevice";
        let transactionFields = "fields[transack]=masterByTrMasterId,trDatetimelocal,trCtrlName,trDevName,";
        transactionFields += "trBuildingName,trFloorName,trZoneName,trSiteName,trTagcode,trDevFixedaddr,trSla,trMasterMstDisplayname,";
        transactionFields += "trOssdDeviceId,trOssdName,";
        transactionFields += "trDevSerial,trCompName,trLocName,trDeptName,trReasoncode,eventType,transackType,unitType,trDirection,dateOnly,trReasonCodeName";
        const eventTypeFields = "fields[eventType]=trnName";
        const transackTypeFields = "fields[transackType]=trnName";
        const unitTypeFields = "fields[unitType]=trnName";
        const sortOrder = "sort=-trDatetimelocal";
        const url = `transack?${includes}&${transactionFields}&${eventTypeFields}&${transackTypeFields}&${unitTypeFields}${paginationString}&${sortOrder}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const transactions: TransactionReport[] = data.map((transaction) => buildTransaction(transaction, profileId, translate));

                    return resolve({ rows: transactions, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getTimeAttendanceTransactionReport = async (listParameters?: ListQuery): Promise<TnaTransactionReportWrapper> => {
    try {
        const newFilters: Filter[] = [];

        listParameters?.filters?.forEach((filter) => {
            if (filter.key === 'dateOnly') {
                const selected = filter.selected.map((selected) => {
                    return {
                        ...selected,
                        value: getUnFormattedDate(selected.value)
                    }
                });

                newFilters.push({ ...filter, selected });
                return;
            }

            newFilters.push(filter);
        })

        const filter: string = getFilterClause(
            newFilters,
            listParameters?.searchTerm
        );
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );

        const url = `bioTransack?include=bioType&fields[bioTransack]=btrCol1,btrSerial,btrTime,dateOnly,btrType,btrKeyName,btrDisplayname,btrFirstname,btrLastname,btrIdnumber,bioType&fields[bioType]=trnName,bioMake${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const transactions: TnaTransactionReport[] = data.map((report) => {
                        return {
                            id: report["id"],
                            name: "",
                            userIdCsn: report["btr-col1"],
                            serialAddress: report["btr-serial"],
                            time: getFormattedTimeFromNum(report["btr-time"]),
                            dateOnly: getFormattedDate(report["date-only"]),
                            button: report["btr-type"],
                            buttonName: report["btr-key-name"],
                            biometricTypeId: report["bio-type"]["id"],
                            biometricType: report["bio-type"]["trn-name"],
                            biometricMake: report["bio-type"]["bio-make"],
                            displayName: report["btr-displayname"],
                            firstName: report["btr-firstname"],
                            lastName: report["btr-lastname"],
                            idNumber: report["btr-idnumber"],
                        };
                    });
                    return resolve({ rows: transactions, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAllReport = async (listParameters?: ListQuery, userProfileId?: number): Promise<ReportWrapper> => {
    try {
        const defaultSortBy = "rptSysname,reportType.trnName,trnName";
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter, defaultSortBy);
        const includes = "include=reportType,userLogin.master,userLogin.site";
        const reportTypes = "fields[reportType]=trnName";
        const reportFields = "fields[report]=trnName,rptDesc,rptSysname,category,supportedForScheduling,reportType,userLogin";
        const userLoginFields = "fields[userLogin]=ulUsername,master,site";
        const siteFields = "fields[site]=siteName";
        const masterFields = "fields[master]=mstDisplayname"
        let url = `report?${includes}&${reportTypes}&${reportFields}&${userLoginFields}&${siteFields}&${masterFields}${partialURL}`;

        // Limit reports available for certain roles
        if (userProfileId && (userProfileId !== ROLES.ID_SYSTEM_OWNER)
            && (userProfileId !== ROLES.ID_SITE_ADMIN)
            && (userProfileId !== ROLES.ID_INSTALLER)
            && (userProfileId !== ROLES.ID_REPORTS)) {

            // hide general mustering report from unsupported roles
            const filterOutReport = `reportType.id=out=(${ReportTypes.MUSTERING})`;
            if (url.includes('&filter')) {
                url = url + ';' + filterOutReport;
            }
            else {
                url = url + '&filter=' + filterOutReport;
            }
        } // TODO: move this to backend instead.

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const reports: Report[] = data.map((report) => {
                        return {
                            id: report["id"],
                            name: report["trn-name"],
                            description: report["rpt-desc"],
                            type: report["report-type"]["trn-name"],
                            createdBy: report["user-login"]["master"] ? report["user-login"]["master"]["mst-displayname"] : report["user-login"]["ul-username"],
                            siteId: report["user-login"]["site"]["id"],
                            siteName: report["user-login"]["site"]["site-name"],
                            status: getReportStatus(report["rpt-sysname"]),
                            typeId: report["report-type"]["id"],
                            nameOriginal: report["trn-name"],
                        };
                    });
                    return resolve({ rows: reports, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject({ message: genericError })
        });
    }
};

export const getAllTransactionReports = async (listParameters?: ListQuery): Promise<ReportWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const includes = "include=reportType,userLogin.master,userLogin.site";
        const reportFields = "fields[report]=trnName,rptDesc,reportType,userLogin,rptSysname";
        const reportTypeFields = "fields[reportType]=trnName";
        const userLoginFields = "fields[userLogin]=master,ulUsername,site";
        const masterFields = "fields[master]=mstDisplayname";
        const siteFields = "fields[site]=siteName";
        let url = `report?${includes}&${reportFields}&${reportTypeFields}&` +
            `${userLoginFields}&${masterFields}&${siteFields}${partialURL}`;
        const filterIn = 'reportType.id=in=(1)&sort=rptSysname,reportType.trnName,trnName';
        if (url.includes('&filter')) {
            url = url + ';' + filterIn;
        }
        else {
            url = url + '&filter=' + filterIn;
        }

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ statusCode: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ statusCode: response.status, message: genericError });
                    }

                    const reports: Report[] = data.map((report) => {
                        return {
                            id: report["id"],
                            name: report["trn-name"],
                            description: report["rpt-desc"],
                            type: report["report-type"]["trn-name"],
                            createdBy: report["user-login"]["master"]
                                ? report["user-login"]["master"][
                                "mst-displayname"
                                ]
                                : report["user-login"]["ul-username"],
                            siteId: report["user-login"]["site"]["id"],
                            siteName: report["user-login"]["site"]["site-name"],
                            status: getReportStatus(report["rpt-sysname"]),
                            typeId: report["report-type"]["id"],
                        };
                    });
                    return resolve({ rows: reports, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject({ message: genericError })
        });
    }
};

export const getAllReportTypeNamesWithPagination = async (
    listParameters?: ListQuery
): Promise<{ rows: Name[]; pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(
            listParameters?.filters,
            listParameters?.searchTerm
        );
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );
        const url = `reportType?${partialURL}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const reportTypes: Name[] = items.map(
                    (reportType): Name => {
                        return {
                            value: reportType["id"],
                            label: reportType["trn-name"],
                        };
                    }
                );
                return resolve({ rows: reportTypes, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const deleteReport = async (
    id: string
): Promise<{ id: string; success: boolean }> => {
    try {
        const url = `report/${id}`;
        const reportType = {
            data: {
                type: "report",
                id: id,
            },
        };
        const response = await apiConnector.deleteData(url, reportType);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true });
            }

            const json = await response.json();

            return reject({ id: id, success: false, status: response.status, message: getConstraintViolation(json.errors[0].detail) });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const storeReport = async (
    report: Report,
    userId: string,
    createReport = true
): Promise<StoreEntityResult> => {
    try {
        let reportSubType;
        if (report.reportSubTypeId) {
            reportSubType = {
                reportSubType: {
                    data: {
                        type: "reportSubType",
                        id: report.reportSubTypeId,
                    },
                },
            }
        }

        const newRecord = {
            data: {
                type: "report",
                id: report.id,
                attributes: {
                    trnName: report.name,
                    rptDesc: report.description,
                    rptHidden: 0,
                    rptSysname: "USER_DEFINED",
                },
                relationships: {
                    reportType: {
                        data: {
                            type: "reportType",
                            id: report.typeId,
                        },
                    },
                    ...reportSubType,
                    userLogin: {
                        data: {
                            type: "userLogin",
                            id: report.userLoginId ? report.userLoginId : userId,
                        },
                    },
                },
            },
        };

        const url = `report${createReport ? "" : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector
            .sendData(url, createReport, newRecord)
            .then((data) => {
                response = data;
            })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createReport) {
                    const content = await response.json();

                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail), success: false })
                    }
                    new Deserializer().deserialize(content, (error, report) => {
                        if (error) {
                            return reject({ status: response.status, id: report.id, success: false });
                        }
                        return resolve({ id: report.id, success: true });
                    })

                    return;
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: report.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: report.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: report.id, success: false, message: genericError });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAllReportNamesWithPagination = async (
    listParameters?: ListQuery,
    userProfileId?: number,
): Promise<{ rows: Name[]; pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(
            listParameters?.filters,
            listParameters?.searchTerm
        );
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );

        let url = `report?sort=rptSysname,trnName${partialURL}`;

        if ((userProfileId !== ROLES.ID_SYSTEM_OWNER) && (userProfileId !== ROLES.ID_SITE_ADMIN) && (userProfileId !== ROLES.ID_INSTALLER)) {
            // hide general mustering report from unsupported roles
            const filterOutReport = `reportType.id=out=(${ReportTypes.MUSTERING})`;
            if (url.includes('&filter')) {
                url = url + ';' + filterOutReport;
            }
            else {
                url = url + '&filter=' + filterOutReport;
            }
        }

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const reportNames: Name[] = data.map((reportName) => {
                        return {
                            value: reportName["id"],
                            label: reportName["trn-name"],
                        };
                    });

                    return resolve({
                        rows: reportNames,
                        pagination: pagination,
                    });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getReportById = async (id: string): Promise<Report> => {
    try {
        const reportFields = "fields[report]=trnName,rptDesc,reportType,userLogin,rptSysname,rptExecDatetime,reportSubType";
        const userLoginFields = "fields[userLogin]=master,ulUsername,site";
        const siteFields = "fields[site]=siteName";
        const masterFields = "fields[master]=mstDisplayname";
        const reportTypeFields = "fields[reportType]=trnName";
        const url = `report/${id}?include=reportType,userLogin.master,userLogin.site,reportSubType&${reportFields}&${userLoginFields}&${siteFields}&${masterFields}&${reportTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    if (data['id']) {
                        const report: Report = {
                            id: data["id"],
                            name: data["trn-name"],
                            description: data["rpt-desc"],
                            type: data["report-type"]["trn-name"],
                            createdBy: data["user-login"]["master"] ? data["user-login"]["master"]["mst-displayname"] : data["user-login"]["ul-username"],
                            siteId: data["user-login"]["site"]["id"],
                            siteName: data["user-login"]["site"]["site-name"],
                            typeId: data["report-type"]["id"],
                            isTemplate: data["rpt-sysname"] === "TEMPLATE" ? true : false,
                            reportSubTypeId: data["report-sub-type"] ? data["report-sub-type"]["id"] : "",
                            lastExecutedTime: data["rpt-exec-datetime"] ? data["rpt-exec-datetime"] : "",
                            userLoginId: data["user-login"]["id"],
                        };

                        return resolve(report);
                    }
                    else {
                        reject(processServerError(error));
                    }
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAccessGroupReport = async (translate?: Translator, listParameters?: ListQuery): Promise<AccessGroupReportWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const accessGroupFields = "&fields[accessGroup]=agName,agType,agMaxCapacity,area,accessTimePattern,site";
        const areaFields = "&fields[area]=areaName";
        const siteFields = "&fields[site]=siteName";
        const accessTimePatternFields = "&fields[accessTimePattern]=atpName,atpDuration,atpMon,atpTue,atpFri,atpHol,atpSat,atpStartTime,atpEndTime,atpSun,atpThu,atpWed";
        const url = "accessGroup?include=accessTimePattern,area,site" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter) + accessGroupFields + areaFields + accessTimePatternFields + siteFields;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (err: any, data: any[]) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const yes = translate ? translate.byKey(TranslationKeys.yes) : "Yes";
                    const no = translate ? translate.byKey(TranslationKeys.no) : "No";

                    const accessGroupReports: AccessGroupReport[] = data.map(
                        (accessGroupReport) => {
                            const accessTimePattern = accessGroupReport["access-time-pattern"];
                            const area = accessGroupReport["area"];
                            const site = accessGroupReport["site"];
                            const agType = accessGroupReport['ag-type'];

                            const groupType = agType === ACCESS_TYPES.ACCESS_GROUP
                                ? (translate ? translate.byKey(TranslationKeys.accessGroup) : "Access Group")
                                : agType === ACCESS_TYPES.FLOOR_GROUP
                                    ? (translate ? translate.byKey(TranslationKeys.floorGroup) : "Floor Group")
                                    : agType === ACCESS_TYPES.OSS_GROUP
                                        ? (translate ? translate.byKey(TranslationKeys.ossGroup) : "OSS Group")
                                        : "-";

                            return {
                                id: accessGroupReport["id"],
                                name: accessGroupReport["ag-name"],
                                accessArea: area?.["area-name"] ?? "-",
                                accessTimePattern: accessTimePattern?.["atp-name"] ?? "-",
                                groupType,
                                duration: accessTimePattern?.["atp-duration"] ? convertToDuration(accessTimePattern["atp-duration"]).toString() : "-",
                                site: site?.["site-name"] ?? "-",
                                maxCapacity: accessGroupReport["ag-max-capacity"] ?? "-",
                                monday: accessTimePattern?.["atp-mon"] ? yes : no,
                                tuesday: accessTimePattern?.["atp-tue"] ? yes : no,
                                friday: accessTimePattern?.["atp-fri"] ? yes : no,
                                holiday: accessTimePattern?.["atp-hol"] ? yes : no,
                                saturday: accessTimePattern?.["atp-sat"] ? yes : no,
                                startTime: accessTimePattern?.["atp-start-time"] !== undefined && accessTimePattern?.["atp-start-time"] !== null ? convertToTime(accessTimePattern["atp-start-time"]).toString() : "-",
                                endTime: accessTimePattern?.["atp-end-time"] !== undefined && accessTimePattern?.["atp-end-time"] !== null ? convertToTime(accessTimePattern["atp-end-time"]).toString() : "-",
                                sunday: accessTimePattern?.["atp-sun"] ? yes : no,
                                thursday: accessTimePattern?.["atp-thu"] ? yes : no,
                                wednesday: accessTimePattern?.["atp-wed"] ? yes : no,
                            };
                        }
                    );

                    return resolve({ rows: accessGroupReports, pagination });
                }
            );
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAreaReport = async (
    listParameters?: ListQuery
): Promise<AreaReportWrapper> => {
    try {
        const filter: string = getFilterClause(
            listParameters?.filters,
            listParameters?.searchTerm
        );
        const includes = "include=area.site,location.zoneByZoneId,location.terminals.building,location.terminals.floor";
        const areaLocationFields = "fields[areaLocation]=location,area";
        const locationFields = "fields[location]=terminals,zoneByZoneId,locName";
        const terminalFields = "fields[terminal]=devDirection,building,floor,site,devFixedAddr";
        const buildingFields = "fields[building]=buildingName";
        const floorFields = "fields[floor]=floorName";
        const siteFields = "fields[site]=siteName";
        const areaFields = "fields[area]=areaName,totalLocations";
        const zoneFields = "fields[zone]=zoneName";
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter).replace("locName", "location.locName"); // need to add replace for column sorting
        const url = `areaLocation?${includes}&${areaLocationFields}&${locationFields}&` +
            `${terminalFields}&${buildingFields}&${floorFields}&${siteFields}&` +
            `${areaFields}&${zoneFields}` + partialURL;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(
                json,
                (err: any, data: any[]) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }
                    const areaReports: AreaReport[] = data.map(
                        (areaReport) => {
                            const readers = areaReport["location"]["terminals"];
                            const entryReader = readers.find(
                                (reader) => reader["dev-direction"] === 0
                            );
                            const exitReader = readers.find(
                                (reader) => reader["dev-direction"] === 1
                            );
                            const building =
                                entryReader && entryReader["building"]
                                    ? entryReader["building"]["building-name"]
                                    : exitReader && exitReader["building"]
                                        ? exitReader["building"]["building-name"]
                                        : undefined;
                            const floor =
                                entryReader && entryReader["floor"]
                                    ? entryReader["floor"]["floor-name"]
                                    : exitReader && exitReader["floor"]
                                        ? exitReader["floor"]["floor-name"]
                                        : undefined;
                            const site =
                                entryReader && entryReader["site"]
                                    ? entryReader["site"]["site-name"]
                                    : exitReader && exitReader["site"]
                                        ? exitReader["site"]["site-name"]
                                        : undefined;
                            return {
                                id: areaReport["id"],
                                name: areaReport["area"]["area-name"],
                                floor: floor,
                                site: site,
                                building: building,
                                zone:
                                    areaReport["location"]["zone-by-zone-id"][
                                    "zone-name"
                                    ],
                                location: areaReport["location"]["loc-name"],
                                entryReader: entryReader
                                    ? entryReader["dev-fixed-addr"]
                                    : "",
                                exitReader: exitReader
                                    ? exitReader["dev-fixed-addr"]
                                    : "",
                                totalLocations:
                                    areaReport["area"]["total-locations"],
                            };
                        }
                    );
                    return resolve({ rows: areaReports, pagination: pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

const getDateRangeQueryString = (listParameters?: ListQuery) => {
    const dateFilter = listParameters?.filters?.find((filter) => filter.key === 'dateOnly');
    const selectedDateValue = dateFilter?.selected?.[0]?.value;
    const [startDate, endDate] = selectedDateValue?.includes(';')
        ? selectedDateValue.split(';').map((value) => value.trim())
        : [selectedDateValue ?? getTodayDate(), selectedDateValue ?? getTodayDate()];

    return { startDate, endDate };
};

export const getAuditReport = async (translate?: Translator, listParameters?: ListQuery): Promise<AuditReportWrapper> => {
    try {
        const { startDate, endDate } = getDateRangeQueryString(listParameters);
        const pageNumber = listParameters?.goToPage ?? 1;
        const pageSize = listParameters?.pageSize ?? 20;

        let url = `custom/reportAudit?include=site&page[number]=${pageNumber}&page[size]=${pageSize}`;
        url += `&filter[startDate]=${startDate}T00:00:00&filter[endDate]=${endDate}T23:59:59`;
        url = appendCustomReportFilters(url, listParameters, ['dateOnly'], undefined, AuditReportColumn);
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            const groupedRows: TableRowGroup<AuditReport>[] = [];
            const records: AuditReport[] = [];
            const reportsBySessionId = new Map<string, any[]>();
            const auditReports = Array.isArray(json.data) ? json.data : [];

            auditReports.forEach((auditReport) => {
                const sessionId = `${auditReport?.attributes?.auditSessionId ?? auditReport?.id ?? ''}`;
                const sessionReports = reportsBySessionId.get(sessionId);

                if (sessionReports) {
                    sessionReports.push(auditReport);
                    return;
                }

                reportsBySessionId.set(sessionId, [auditReport]);
            });

            reportsBySessionId.forEach((sessionReports, sessionId) => {
                const parentAuditReport = sessionReports[0];
                const sessionDetails = getAuditSessionDetails(sessionReports);
                const childRows = buildAuditSessionChildRows(sessionDetails);
                const parentRow = buildAuditSessionParentRow(parentAuditReport, sessionDetails.length);

                groupedRows.push({
                    id: sessionId,
                    parentRow,
                    childRows,
                    childCount: sessionDetails.length,
                    initiallyCollapsed: true,
                });

                records.push(parentRow, ...childRows);
            });

            return resolve({ rows: records, groupedRows, pagination });
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const getHardwareInstallationReport = async (
    listParameters?: ListQuery
): Promise<HardwareReportWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        let partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        partialURL = partialURL.replace("hdtName", "type");
        const reportHardwareInstallationFields = "fields[reportHardwareInstallation]=address,devDesc,devName,enabled,location,tableTypeName,type,site,zone,building,floor";
        const locationFields = "fields[location]=locDesc,locName";
        const buildingFields = "fields[building]=buildingName";
        const zoneFields = "fields[zone]=zoneName";
        const floorFields = "fields[floor]=floorName";
        const siteFields = "fields[site]=siteName";
        const url = `reportHardwareInstallation?include=site,zone,location,building,floor${partialURL}&${reportHardwareInstallationFields}&${locationFields}&${buildingFields}&${zoneFields}&${floorFields}&${siteFields}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const hadrwareInstallationReports: HardwareInstallationReport[] = data.map(
                        (report) => {
                            return {
                                id: report["id"],
                                address: report["address"],
                                deviceDescription: report["dev-desc"],
                                name: report["dev-name"],
                                enabled: report["enabled"] === 1 ? "Yes" : "No",
                                locDescription: report.location ? report.location["loc-desc"] : "",
                                locName: report.location ? report.location["loc-name"] : "",
                                tableTypeName: report["table-type-Name"],
                                type: report["type"],
                                zoneName: report.zone ? report.zone["zone-name"] : "",
                                buildingName: report.building ? report.building["building-name"] : "",
                                floorName: report.floor ? report.floor["floor-name"] : "",
                                siteName: report.site ? report.site["site-name"] : ""
                            };
                        }
                    );

                    return resolve({
                        rows: hadrwareInstallationReports,
                        pagination,
                    });
                }
            );
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const getExpiredCredentialReports = async (profileId: number, translate: Translator, listParameters?: ListQuery): Promise<ExpiredCredentialWrapper> => {
    try {
        const filter: string = getFilterClause(
            listParameters?.filters,
            listParameters?.searchTerm
        );
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );
        let reportFields = "fields[reportExpiredCards]=mstDisplayname,mstIdnumber,";
        reportFields += "mstSuffix,expDateDisplay,tagCode,expired,compName,deptName,";
        reportFields += "mtName,mstFirstname,mstMiddlename,mstLastname,mstTitle,";
        reportFields += "ttNameOriginal,profileName,expTime";
        const url = `reportExpiredCards?${reportFields}&${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const credentialReports: ExpiredCredentialReport[] = data.map(
                        (credential) => {
                            return {
                                id: credential["id"],
                                displayName: credential["mst-displayname"],
                                idNumber: credential["mst-idnumber"],
                                suffix: credential["mst-suffix"],
                                expiryDate: credential["exp-date-display"],
                                tagCode: showTagCode(
                                    profileId,
                                    credential["tag-code"]
                                ),
                                expired: credential["expired"] === 1 ? translate.byKey(TranslationKeys.yes) : translate.byKey(TranslationKeys.no),
                                companyName: credential["comp-name"],
                                departmentName: credential["dept-name"],
                                type: credential["mt-name"],
                                firstName: credential["mst-firstname"],
                                middleName: credential["mst-middlename"],
                                lastName: credential["mst-lastname"],
                                title: credential["mst-title"],
                                tagTypeName: credential["tt-trn-name"],
                                category: credential["profile-name"],
                                expiryTime: credential["exp-time"],
                            };
                        }
                    );
                    return resolve({ rows: credentialReports, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

const buildPersonImages = (images?) => {
    let masterImages: PersonImage[] = [];
    if (images && images.length > 0) {
        images.map((image) => {
            masterImages.push({
                base64Image: image["base64-image"],
                base64Thumb: image["base64-thumb"],
                id: image["id"],
                masterTypeId: parseInt(image["master-image-type"]["id"]),
            });
        });
    }

    return masterImages;
};

const getGenderLabel = (gender: string, translate: Translator) => {
    if (gender === 'F') {
        return translate.byKey(TranslationKeys.female);
    }
    else if (gender === 'M') {
        return translate.byKey(TranslationKeys.male);
    }
    else {
        return translate.byKey(TranslationKeys.unspecified);
    }
}

export const getFirstInLastOutReport = async (listParameters?: ListQuery): Promise<any> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const includes = "include=master.profile,master.company,master.department";
        let url = `reportFirstInLastOut?${includes}` +
            createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(
                json,
                (err: any, response: any) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }
                    let newDays;
                    const firstInLastOutReport: FirstInLastOutReport[] = response.map(
                        (firstInLastOutReport, i) => {
                            const hasDepartment = (response[i].master.department != null);
                            const hasCompany = (response[i].master.company != null);
                            newDays = buildFirstInLastOutWeekDays(firstInLastOutReport)
                            return {
                                id: firstInLastOutReport["id"],
                                firstName: firstInLastOutReport['master']['mst-firstname'],
                                masterId: firstInLastOutReport['master']["id"],
                                displayName: firstInLastOutReport['master']["mst-displayname"],
                                uniqueIdentifier: firstInLastOutReport['master']['mst-idnumber'],
                                lastName: firstInLastOutReport['master']['mst-lastname'],
                                middleName: firstInLastOutReport['master']['mst-middlename'],
                                suffix: firstInLastOutReport['master']['mst-suffix'],
                                title: firstInLastOutReport['master']['mst-title'],
                                categoryType: firstInLastOutReport['master']['profile']['trn-name'],
                                profileId: firstInLastOutReport['master']['profile']['id'],
                                compName: !hasCompany ? '' : firstInLastOutReport['master']['company']['comp-name'],
                                deptName: !hasDepartment ? '' : firstInLastOutReport['master']['department']['dept-name'],
                                ...(convertArrayFirstInLastOutDaysToObject(buildFirstInLastOutWeekDays(firstInLastOutReport)))
                            };
                        }
                    );
                    return resolve({ rows: firstInLastOutReport, pagination, dynamicColumns: newDays });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getFirstInLastOutReportByReportType = async (reportType: number, listParameters?: ListQuery): Promise<any> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const filterRowType = "filter=rowType==" + `${reportType}`;
        const updatedFilters = filter.length > 0
            ? filter.replace("filter=", `${filterRowType};`)
            : `&${filterRowType}`
        const includes = "include=master.profile,master.company,master.department";
        let reportFields = "fields[reportFirstInLastOut]=master,day01,day02,day03,day04,";
        reportFields += "day05,day06,day07,day08,day09,day10,day11,day12,day13,day14,day15,";
        reportFields += "day16,day17,day18,day19,day20,day21,day22,day23,day24,day25,day26,";
        reportFields += "day27,day28,day29,day30,day31";
        let masterFields = "fields[master]=mstFirstname,mstDisplayname,mstIdnumber,";
        masterFields += "mstLastname,mstMiddlename,mstSuffix,mstTitle,profile,company,department";
        const profileFields = "fields[profile]=trnName";
        const companyFields = "fields[company]=compName";
        const departmentFields = "fields[department]=deptName";
        let url = `reportFirstInLastOut?${includes}&${reportFields}&${masterFields}&`;
        url += `${profileFields}&${companyFields}&${departmentFields}`;
        url += createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, updatedFilters);
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (err: any, response: any) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }
                    const firstInLastOutReport: FirstInLastOutReport[] = response.map(
                        (firstInLastOutReport, i) => {
                            const hasDepartment = (response[i].master.department != null);
                            const hasCompany = (response[i].master.company != null);
                            return {
                                id: firstInLastOutReport["id"],
                                firstName: firstInLastOutReport['master']['mst-firstname'],
                                masterId: firstInLastOutReport['master']["id"],
                                displayName: firstInLastOutReport['master']["mst-displayname"],
                                uniqueIdentifier: firstInLastOutReport['master']['mst-idnumber'],
                                lastName: firstInLastOutReport['master']['mst-lastname'],
                                middleName: firstInLastOutReport['master']['mst-middlename'],
                                suffix: firstInLastOutReport['master']['mst-suffix'],
                                title: firstInLastOutReport['master']['mst-title'],
                                categoryType: firstInLastOutReport['master']['profile']['trn-name'],
                                profileId: firstInLastOutReport['master']['profile']['id'],
                                compName: !hasCompany ? '' : firstInLastOutReport['master']['company']['comp-name'],
                                deptName: !hasDepartment ? '' : firstInLastOutReport['master']['department']['dept-name'],
                                ...(convertArrayFirstInLastOutDaysToObject(buildFirstInLastOutWeekDays(firstInLastOutReport)))
                            };
                        }
                    );
                    return resolve({ rows: firstInLastOutReport, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getFirstInLastOutReportByMasterId = async (masterId?: any, listParameters?: ListQuery): Promise<any> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const includes = "include=master.profile,master.company,master.department";
        const filterByPerson = `filter=master.id==${masterId}`;
        let url = `reportFirstInLastOut?${includes}&${filterByPerson}` +
            createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(
                json,
                (err: any, response: any) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const firstInLastOutReport: FirstInLastOutReport[] = response.map(
                        (firstInLastOutReport, i) => {
                            const hasDepartment = (response[i].master.department != null);
                            const hasCompany = (response[i].master.company != null);
                            return {
                                id: firstInLastOutReport["id"],
                                firstName: firstInLastOutReport['master']['mst-firstname'],
                                masterId: firstInLastOutReport['master']["id"],
                                displayName: firstInLastOutReport['master']["mst-displayname"],
                                uniqueIdentifier: firstInLastOutReport['master']['mst-idnumber'],
                                lastName: firstInLastOutReport['master']['mst-lastname'],
                                middleName: firstInLastOutReport['master']['mst-middlename'],
                                suffix: firstInLastOutReport['master']['mst-suffix'],
                                title: firstInLastOutReport['master']['mst-title'],
                                categoryType: firstInLastOutReport['master']['profile']['trn-name'],
                                profileId: firstInLastOutReport['master']['profile']['id'],
                                compName: !hasCompany ? '' : firstInLastOutReport['master']['company']['comp-name'],
                                deptName: !hasDepartment ? '' : firstInLastOutReport['master']['department']['dept-name'],
                                ...(convertArrayFirstInLastOutDaysToObject(buildFirstInLastOutWeekDays(firstInLastOutReport)))
                            };
                        }
                    );
                    return resolve({ rows: firstInLastOutReport, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};


function convertArrayFirstInLastOutDaysToObject(arr) {
    const object = arr.reduce((obj, item) => ({
        ...obj,
        [item.accessor]: item.time
    }), {});

    return object;
}

export const getAbsenteeismReport = async (absentTranslatedValue: string, translate: Translator, listParameters?: ListQuery): Promise<AbsenteeismReportWrapper> => {
    try {
        // Derive startDate / endDate from dateOnly filter if present, otherwise fall back to
        // current week Monday-Sunday.
        const dateOnlyFilter = listParameters?.filters?.find(f => f.key === 'dateOnly');
        const selectedDate = dateOnlyFilter?.selected?.[0]?.value;

        let startDate: string;
        let endDate: string;
        if (selectedDate) {
            [startDate, endDate] = selectedDate.includes(';') ? selectedDate.split(';').map(v => v.trim()) : [selectedDate, selectedDate];
        } else {
            const weekRange = getCurrentWeekDates(new Date()).split(';');
            startDate = weekRange[0];
            endDate = weekRange[1];
        }

        // datesToCheckAgainst is used to map absence dates back to day-of-week column names.
        const datesToCheckAgainst = getDatesBetween(startDate, endDate);

        const pageNumber = listParameters?.goToPage ?? 1;
        const pageSize = listParameters?.pageSize ?? 20;

        let url = `custom/reportAbsenteeism?include=site&page[number]=${pageNumber}&page[size]=${pageSize}`;
        url += `&filter[startDate]=${startDate}&filter[endDate]=${endDate}`;
        url = appendCustomReportFilters(url, listParameters, ['dateOnly']);

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const absenteeismRecords = data.map(absenteeism => {
                    const record: AbsenteeismReport = {
                        id: absenteeism["id"],
                        uniqueIdentifier: absenteeism["mst-idnumber"],
                        title: absenteeism["mst-title"],
                        displayName: absenteeism["mst-displayname"],
                        firstName: absenteeism["mst-firstname"],
                        middleName: absenteeism["mst-middlename"],
                        lastName: absenteeism["mst-lastname"],
                        suffix: absenteeism["mst-suffix"],
                        current: absenteeism["mst-current"] === 1 ? translate.byKey(TranslationKeys.yes) : translate.byKey(TranslationKeys.no),
                        gender: getGenderLabel(absenteeism["mst-gender"], translate),
                        mtName: absenteeism["mt-name"],
                        department: absenteeism["dept-name"],
                        company: absenteeism["comp-name"],
                        category: absenteeism["p-name-original"],
                        site: absenteeism["site"]["site-name"],
                        monday: "-",
                        tuesday: "-",
                        wednesday: "-",
                        thursday: "-",
                        friday: "-",
                        saturday: "-",
                        sunday: "-",
                    };

                    convertDatesToWeekDays(absenteeism["absence-dates"], datesToCheckAgainst).forEach((day) => {
                        record[day] = absentTranslatedValue;
                    });

                    return record;
                });

                return resolve({ rows: absenteeismRecords, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getZoneOccupancyReport = async (profileId: number, listParameters?: ListQuery): Promise<ZoneOccupancykWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const reportZoneOccupancyFields = "&fields[reportZoneOccupancy]=site,mstDisplayname,deptName,siteName,lastTrTagcode,lastTrDateTime,mstIdnumber,departmentId,commonZoneName,commonZoneId,zoneName,zoneId,compName,companyId,mstFirstname,mstMiddlename,mstLastname,mstTitle,mstSuffix";
        const siteFields = "&fields[site]=siteName";
        const url = "reportZoneOccupancy?" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter) + reportZoneOccupancyFields + siteFields;
        const updatedDepartmentFilter = url.replace('department.id', 'departmentId');
        const updatedZoneFilter = updatedDepartmentFilter.replace('zone.id', 'zoneId');
        const updatedCompanyFilter = updatedZoneFilter.replace('company.id', 'companyId');

        const response = await apiConnector.fetchData(updatedCompanyFilter);
        const json = await response.json();
        const pagination = createPagination(json.meta);


        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(
                json,
                (err: any, data: any[]) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const zoneOccupancyReport: ZoneOccupancy[] = data.map((zoneOccupancy) => {
                        return {
                            id: zoneOccupancy["id"],
                            displayName: zoneOccupancy["mst-displayname"],
                            department: zoneOccupancy["dept-name"],
                            site: zoneOccupancy["site-name"],
                            tagCode: showTagCode(profileId, zoneOccupancy["last-tr-tagcode"]),
                            dateTime: zoneOccupancy["last-tr-date-time"].replace("T", " ").replace(".000", ""),
                            idNumber: zoneOccupancy["mst-idnumber"],
                            siteId: zoneOccupancy["site-id"],
                            departmentId: zoneOccupancy["department-id"],
                            commonZone: zoneOccupancy["common-zone-name"],
                            commonZoneId: zoneOccupancy["common-zone-id"],
                            zone: zoneOccupancy["zone-name"],
                            zoneId: zoneOccupancy["zone-id"],
                            company: zoneOccupancy["comp-name"],
                            companyId: zoneOccupancy["company-id"],
                            firstName: zoneOccupancy["mst-firstname"],
                            middleName: zoneOccupancy["mst-middlename"],
                            lastName: zoneOccupancy["mst-lastname"],
                            title: zoneOccupancy["mst-title"],
                            suffix: zoneOccupancy["mst-suffix"],
                        };
                    });
                    return resolve({ rows: zoneOccupancyReport, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getHoursWorkedReport = async (translate: Translator, listParameters?: ListQuery): Promise<HoursWorkedReportWrapper> => {
    try {
        // Date filter
        const dateFilter = listParameters?.filters?.find(f => f.key === 'dateOnly');
        const selectedDate = dateFilter?.selected?.[0]?.value;
        const [startDate, endDate] = selectedDate?.includes(';')
            ? selectedDate.split(';').map(v => v.trim())
            : [selectedDate ?? getTodayDate(), selectedDate ?? getTodayDate()];

        // Site filter
        const siteFilter = listParameters?.filters?.find(f => f.key === 'site.id');
        const siteIds = siteFilter?.selected?.filter(s => s.ignore !== true).map(s => s.value) ?? [];

        const pageNumber = listParameters?.goToPage ?? 1;
        const pageSize = listParameters?.pageSize ?? 20;

        let url = `custom/reportWorkingHours?page[number]=${pageNumber}&page[size]=${pageSize}`;
        url += `&filter[startDate]=${startDate}&filter[endDate]=${endDate}`;
        if (siteIds.length > 0) {
            url += `&filter[siteId]=[${siteIds.join(',')}]`;
        }
        url = appendCustomReportFilters(url, listParameters, ['dateOnly', 'site.id']);

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            const groupedRows: TableRowGroup<HoursWorkedReport>[] = [];
            const records: HoursWorkedReport[] = [];
            const hwItems: any[] = Array.isArray(json.data) ? json.data : [];

            hwItems.forEach((item) => {
                const attr = item.attributes ?? item;
                const dailyDetails: any[] = Array.isArray(attr.dailyDetails) ? attr.dailyDetails : [];
                const parentRow = buildHoursWorkedParentRow(item, dailyDetails.length);
                const childRows = dailyDetails.map((detail, i) => buildHoursWorkedChildRow(detail, item.id, i));

                groupedRows.push({
                    id: item.id,
                    parentRow,
                    childRows,
                    childCount: dailyDetails.length,
                    initiallyCollapsed: true,
                });

                records.push(parentRow, ...childRows);
            });

            return resolve({ rows: records, groupedRows, pagination });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAllTemplateReportTypeNamesWithPagination = async (listParameters?: ListQuery, userProfileId?: number): Promise<{ rows: any[]; pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(
            listParameters?.filters,
            listParameters?.searchTerm
        );
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );
        let url = `report?include=reportType,reportSubType&sort=rptSysname,reportType.trnName,trnName${partialURL}`;

        if ((userProfileId !== ROLES.ID_SYSTEM_OWNER) && (userProfileId !== ROLES.ID_SITE_ADMIN) && (userProfileId !== ROLES.ID_INSTALLER)) {
            // hide general mustering report from unsupported roles
            const filterOutReport = `reportType.id=out=(${ReportTypes.MUSTERING})`;
            url = url + ';' + filterOutReport;
        }

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const reportTypes: any[] = data.map(
                        (reportType): any => {
                            return {
                                value: reportType["id"],
                                label: reportType["trn-name"],
                                reportType: reportType["report-type"]["id"],
                                reportSubType: reportType["report-sub-type"] ? reportType["report-sub-type"]["id"] : null,
                            };
                        }
                    );

                    return resolve({ rows: reportTypes, pagination: pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject({ message: genericError })
        });
    }
};

export const getMusteringReport = async (translate: Translator, listParameters?: ListQuery): Promise<MusteringReportReportWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const reportGeneralMusteringFields = "&fields[reportGeneralMustering]=mstIdnumber,mstDisplayname,mstFirstname,mstLastname,mstSuffix,mobileNumber,deviceName,site,locName,time,status,department,company,profile";
        const departmentFields = "&fields[department]=deptName";
        const companyFields = "&fields[company]=compName";
        const profileFields = "&fields[profile]=trnName";
        const siteFields = "&fields[site]=siteName";
        const url = "reportGeneralMustering?include=department,company,site,profile" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter) + reportGeneralMusteringFields + departmentFields + companyFields + profileFields + siteFields;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (err: any, response: any) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const musteringReportData: MusteringReport[] = response.map((data) => {
                    return {
                        id: data["id"],
                        idNumber: data["mst-idnumber"],
                        displayName: data["mst-displayname"],
                        status: getStatusDescription(data["status"], translate),
                        lastLocation: data["loc-name"],
                        lastSeenTime: data["time"],
                        department: data["department"] ? data["department"]["dept-name"] : "",
                        company: data["company"] ? data["company"]["comp-name"] : "",
                        firstName: data["mst-firstname"],
                        lastName: data["mst-lastname"],
                        suffix: data["mst-suffix"],
                        contactNumber: data["mobile-number"] ? data["mobile-number"] : "",
                        site: data["site"] ? data["site"]["site-name"] : "",
                        profile: data["profile"] ? data["profile"]["trn-name"] : "",
                        deviceName: data["device-name"] ? data["device-name"] : "",
                        statusValue: data["status"],
                    }
                });

                return resolve({ rows: musteringReportData, pagination: pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

const getStatusDescription = (status: number, translate: Translator) => {
    switch (status) {
        case 1:
            return getMusterReportStatus("ABSENT", translate);
        case 2:
            return getMusterReportStatus("MUSTERED", translate);
        case 3:
            return getMusterReportStatus("MANUALLY_MUSTERED", translate);
        case 4:
            return getMusterReportStatus("UNACCOUNTED", translate);
        case 5:
            return getMusterReportStatus("EXITED", translate);
        default:
            return getMusterReportStatus("ABSENT", translate);
    }
};

export const getMusteringReportTotals = async (siteId: string, reportId: number): Promise<MusteringReportTotals> => {
    try {
        const url = '';
        const headers = {
            type: "generalMusteringReportTotal",
            siteId: siteId,
            reportId: reportId,
        }
        const response = await apiConnector.fetchData(url, true, headers);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: genericError });
            }

            const totals: MusteringReportTotals = {
                absent: json["absent"],
                exited: json["exited"],
                manuallyMustered: json["manuallyMustered"],
                mustered: json["mustered"],
                totalMustered: json["totalMustered"],
                totalOnSite: json["totalOnSite"],
                unaccounted: json["unaccounted"],
                unknown: json["unknown"],
                unaccountedWithExited: (json["unaccounted"] + json["exited"])
            }

            return resolve(totals);
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getStillOnSiteReport = async (translate: Translator, warn: ToastFunction, listParameters?: ListQuery): Promise<StillOnSiteWrapper> => {
    try {

        // Must have exit points selected to run the report.
        const siteExitFilter = listParameters?.filters?.find(filter => filter.key === "transacksForTrMasterId.terminal.id");
        if (!!!siteExitFilter || siteExitFilter.selected.length < 0 || siteExitFilter.selected.filter(selected => selected.ignore !== true).length <= 0) {
            return new Promise((resolve, reject) => {
                warn(translate.byKey(TranslationKeys.siteExitMessage));
                resolve({ rows: [], pagination: { maxPageSize: 0, pageNumber: 0, totalCount: 0, totalPages: 0 } });
            });
        }

        // The time filter
        const origTimeFilter = listParameters?.filters?.find(filter => filter.key === "time");
        const timeSuffix = origTimeFilter?.selected?.[0]?.value ? `T${origTimeFilter.selected[0].value}:00` : 'T00:00:00';

        // The date to check for who is still on site, default to today if not specified
        const origDateFilter = listParameters?.filters?.find(filter => filter.key === "dateOnly");
        const specifiedDateTime = origDateFilter?.selected?.[0]?.value
            ? `${origDateFilter.selected[0].value}${timeSuffix}`
            : `${getTodayDate()}${timeSuffix}`;

        // How many hours back to look for exit transactions, default to 24 if not specified
        const lookbackHours = 24;

        // The site to run the report for.
        const siteFilterValues = listParameters?.filters?.find(filter => filter.key === "site.id")?.selected?.filter(s => s.ignore !== true);
        const siteId = siteFilterValues?.[0]?.value ?? '2';

        // The exit points which indicate when someone has left the site.
        const exitDoors = listParameters?.filters?.find(filter => filter.key === "transacksForTrMasterId.terminal.id");
        const exitDoorIds = exitDoors?.selected?.filter(s => s.ignore !== true).map(s => s.value) ?? [];

        const pageNumber = listParameters?.goToPage ?? 1;
        const pageSize = listParameters?.pageSize ?? 20;

        let url = `custom/reportStillOnSite?page[number]=${pageNumber}&page[size]=${pageSize}`;
        url += `&filter[specifiedDateTime]=${encodeURIComponent(specifiedDateTime)}`;
        url += `&filter[lookbackHours]=${lookbackHours}`;
        url += `&filter[siteId]=${siteId}`;
        if (exitDoorIds.length > 0) {
            url += `&filter[exitDoorIds]=${exitDoorIds.join(',')}`;
        }
        url = appendCustomReportFilters(url, listParameters, ['transacksForTrMasterId.terminal.id', 'dateOnly', 'time', 'lookbackHours', 'site.id']);

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, masters: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }
                const masterStillOnSite: StillOnSite[] = masters.map((master): StillOnSite => {
                    return {
                        id: master["master-id"],
                        name: master["mst-displayname"],
                        masterId: master['master-id'],
                        firstName: master['mst-firstname'],
                        lastName: master['mst-lastname'],
                        middleName: master['mst-middlename'],
                        email: master["mst-email-address"],
                        suffix: master["mst-suffix"],
                        title: master["mst-title"],
                        uid: master["mst-idnumber"],
                        mobileNumber: master["mst-mobile-address"],

                        compName: master['comp-name'] ? master['comp-name'] : '',
                        companyId: master['company-id'],
                        deptName: master['dept-name'] ? master['dept-name'] : '',
                        departmentId: master['department-id'],
                        category: master['profile-name-original'] ? master['profile-name-original'] : '',
                        profileId: master['profile-id'],

                        firstIn: master['first-in-time'] ? getFormattedTime(getTime(new Date(master['first-in-time']))) : '',
                        lastLocation: master['last-seen-location'] ? master['last-seen-location'] : '',
                        lastSeen: master['last-seen-time'] ? getFormattedTime(getTime(new Date(master['last-seen-time']))) : '',
                    }
                });

                return resolve({ rows: masterStillOnSite, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getCardsNotUsedReport = async (listParameters?: ListQuery): Promise<CardsNotUsedReportWrapper> => {
    try {
        const pageNumber = listParameters?.goToPage ?? 1;
        const pageSize = listParameters?.pageSize ?? 20;
        const siteFilter = listParameters?.filters?.find(f => f.key === 'site.id');
        const siteIds = siteFilter?.selected?.filter(s => s.ignore !== true).map(s => s.value) ?? [];
        let url = `custom/reportTagsNotUsed?page[number]=${pageNumber}&page[size]=${pageSize}`;

        if (siteIds.length > 0) {
            url += `&filter[siteId]=${siteIds.join(',')}`;
        }

        const inactiveForFilter = listParameters?.filters?.find(f => f.key === 'inactiveFor');
        const inactiveForSysname = inactiveForFilter?.selected?.[0]?.elementSysname ?? FILTER_INACTIVE_DAYS_90;
        const days = INACTIVE_DAYS_BY_SYSNAME[inactiveForSysname] ?? '90';

        url += `&filter[days]=${days}`;
        url = appendCustomReportFilters(url, listParameters, ['site.id', 'inactiveFor']);

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const cardsNotUsedReport: CardsNotUsedReport[] = data.map((record) => {
                    return {
                        id: record["id"],
                        tagId: record["tag-id"],
                        tagCode: record["tag-code"],
                        tagCodeUntruncated: record["tag-code-untruncated"],
                        tagType: record["tag-type-name"],
                        displayName: record["mst-displayname"],
                        firstName: record["mst-firstname"],
                        lastName: record["mst-lastname"],
                        title: record["mst-title"],
                        middleName: record["mst-middlename"],
                        suffix: record["mst-suffix"],
                        uniqueIdentifier: record["mst-idnumber"],
                        masterId: record["master-id"],
                        companyId: record["company-id"],
                        company: record["comp-name"],
                        departmentId: record["department-id"],
                        department: record["dept-name"],
                        site: record["site"] ? record["site"]["site-name"] : "",
                        lastUsedDate: record["last-used-date"],
                        daysInactive: record["days-inactive"],
                    };
                });

                return resolve({ rows: cardsNotUsedReport, pagination });
            });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const getVingcardTransactionReport = async (listParameters?: ListQuery): Promise<VingcardTransactionReportWrapper> => {
    try {
        const newFilters: Filter[] = [];
        listParameters?.filters?.forEach((filter) => {
            if (filter.key === 'dateOnly') {
                const selected = filter.selected.map((selected) => {
                    return {
                        ...selected,
                        value: getUnFormattedDate(selected.value)
                    }
                });

                newFilters.push({ ...filter, selected });
                return;
            }

            newFilters.push(filter);
        })

        const filter: string = getFilterClause(
            newFilters,
            listParameters?.searchTerm
        );
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );

        const url = `vingCardTransaction?${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const transactions: VingcardTransactionReport[] = data.map((report) => {
                        return {
                            id: report["id"],
                            name: "",
                            eventTime: getFormattedDateTime(report["event-time"]),
                            dateOnly: getFormattedDate(report["date-only"]),
                            timeOnly: getFormattedTimeFromNum(report["time-only"]),
                            registrationNumber: report["registration-number"],
                            cardNameID: report["card-name-id"],
                            doorId: report["door-id"],
                            eventCode: report["event-code"],
                            eventDescription: report["event-discription"],
                        };
                    });
                    return resolve({ rows: transactions, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getGeneralAccessReport = async (translate: Translator, warn: ToastFunction, listParameters?: ListQuery): Promise<GeneralAccessReportWrapper> => {
    try {
        const pageNumber = listParameters?.goToPage ?? 1;
        const pageSize = listParameters?.pageSize ?? 20;
        let url = `custom/reportPersonAccessGroups?page[number]=${pageNumber}&page[size]=${pageSize}`;
        url = appendCustomReportFilters(url, listParameters)
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail)});
            }

            const groupedRows: TableRowGroup<any>[] = [];
            const records: any[] = [];
            const items: any[] = Array.isArray(json.data) ? json.data : [];

            items.forEach((item) => {
                const attr = item.attributes ?? item;
                const parentRow = {
                    id: item.id,
                    rowType: 'parent',
                    masterId: attr["masterId"],
                    displayName: attr["mstDisplayname"],
                    firstName: attr["mstFirstname"],
                    lastName: attr["mstLastname"],
                    site: attr["siteName"],
                    company: attr["compName"],
                    department: attr["deptName"],
                    siteId: attr["siteId"],
                    companyId: attr["companyId"],
                    departmentId: attr["departmentId"],
                };

                const accessGroups: any[] = Array.isArray(attr["accessGroups"]) ? attr["accessGroups"] : [];
                const childRows = accessGroups.map((ag: any, i: number) => ({
                    id: `${item.id}-ag-${i}`,
                    rowType: 'child',
                    accessGroupName: ag["accessGroupName"],
                    accessGroupId: ag["accessGroupId"],
                    accessGroupSite: ag["accessGroupSiteName"],
                    accessGroupSiteId: ag["accessGroupSiteId"],
                    accessTimePatternName: ag["accessTimePatternName"],
                    accessTimePatternId: ag["accessTimePatternId"]
                }));

                groupedRows.push({
                    id: item.id,
                    parentRow,
                    childRows,
                    childCount: childRows.length,
                    initiallyCollapsed: true,
                });

                records.push(parentRow, ...childRows);
            });

            return resolve({rows: records, groupedRows, pagination});
        });
    }
    catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const getDoorAccessReport = async (translate: Translator, warn: ToastFunction, listParameters?: ListQuery): Promise<DoorAccessReportWrapper> => {
    try {
        const doorFilter = listParameters?.filters?.find(filter => filter.key === "locationId");
        const doorIds = doorFilter?.selected?.filter(selected => selected.ignore !== true).map(selected => selected.value) ?? [];
        const pageNumber = listParameters?.goToPage ?? 1;
        const pageSize = listParameters?.pageSize ?? 20;

        if (doorIds.length === 0) {
            warn(translate.byKey(TranslationKeys.pleaseSelectDoor));
            return { rows: [], pagination: { maxPageSize: 0, pageNumber: 0, totalCount: 0, totalPages: 0 }}
        }

        let url = `custom/reportDoorAccess?filter[locationId]=${doorIds[0]}&page[number]=${pageNumber}&page[size]=${pageSize}`;
        url = appendCustomReportFilters(url, listParameters, ['locationId'])
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail)});
            }

            const groupedRows: TableRowGroup<any>[] = [];
            const records: any[] = [];
            const items: any[] = Array.isArray(json.data) ? json.data : [];

            items.forEach((item) => {
                const attr = item.attributes ?? item;
                const parentRow = {
                    id: item.id,
                    rowType: 'parent',
                    masterId: attr["masterId"],
                    title: attr["mstTitle"],
                    uid: attr["mstIdnumber"],
                    displayName: attr["mstDisplayname"],
                    firstName: attr["mstFirstname"],
                    lastName: attr["mstLastname"],
                    middleName: attr["mstMiddlename"],
                    site: attr["siteName"],
                    suffix: attr["mstSuffix"],
                    company: attr["compName"],
                    department: attr["deptName"],
                    siteId: attr["siteId"],
                    companyId: attr["companyId"],
                    departmentId: attr["departmentId"],
                };

                const accessGroups: any[] = Array.isArray(attr["accessGroups"]) ? attr["accessGroups"] : [];
                const childRows = accessGroups.map((ag: any, i: number) => ({
                    id: `${item.id}-ag-${i}`,
                    rowType: 'child',
                    accessGroup: ag["accessGroupName"],
                    timePattern: ag["accessTimePatternName"],
                    startTime: ag["atpStartTime"],
                    duration: ag["atpDuration"],
                    days: ag["days"],
                }));

                groupedRows.push({
                    id: item.id,
                    parentRow,
                    childRows,
                    childCount: childRows.length,
                    initiallyCollapsed: true,
                });

                records.push(parentRow, ...childRows);
            });

            return resolve({rows: records, groupedRows, pagination});
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const getPersonAccessReport = async (translate: Translator, warn: ToastFunction, listParameters?: ListQuery): Promise<PersonAccessReportWrapper> => {
    try {
        const personFilter = listParameters?.filters?.find(filter => filter.key === "masterId");
        const personIds = personFilter?.selected?.filter(selected => selected.ignore !== true).map(selected => selected.value) ?? [];
        const pageNumber = listParameters?.goToPage ?? 1;
        const pageSize = listParameters?.pageSize ?? 20;

        if (personIds.length === 0) {
            warn(translate.byKey(TranslationKeys.pleaseSelectPerson));
            return { rows: [], pagination: { maxPageSize: 0, pageNumber: 0, totalCount: 0, totalPages: 0 } };
        }

        let url = `custom/reportPersonAccess?filter[masterId]=${personIds[0]}&page[number]=${pageNumber}&page[size]=${pageSize}`;
        url = appendCustomReportFilters(url, listParameters, ['masterId']);
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        if (json.errors) {
            return Promise.reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
        }

        const pagination = createPagination(json.meta);
        const items: any[] = Array.isArray(json.data) ? json.data : [];
        const rows: PersonAccessReport[] = items.map((item) => {
            const attr = item.attributes ?? item;
            return {
                id: item.id,
                masterId: attr["masterId"],
                locationId: attr["locationId"],
                accessGroupId: attr["accessGroupId"],
                siteId: attr["siteId"],
                areaId: attr["areaId"],
                buildingId: attr["buildingId"],
                floorId: attr["floorId"],
                site: attr["siteName"],
                area: attr["areaName"],
                door: attr["locationName"],
                building: attr["buildingName"],
                floor: attr["floorName"],
                accessGroup: attr["accessGroupName"],
            };
        });

        return { rows, pagination };

    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const getSequentialEventReport = async (translate: Translator, warn: ToastFunction, listParameters?: ListQuery): Promise<SequentialEventReportWrapper> => {
    try {
        const entryTerminalFilter = listParameters?.filters?.find(filter => filter.key === "entryTerminalId");
        const passTerminalFilter = listParameters?.filters?.find(filter => filter.key === "passTerminalId");
        const failTerminalFilter = listParameters?.filters?.find(filter => filter.key === "failTerminalId");
        const timeoutMinutesFilter = listParameters?.filters?.find(filter => filter.key === "timeoutMinutes");
        const dateFilter = listParameters?.filters?.find(filter => filter.key === "dateOnly");

        const entryTerminalId = entryTerminalFilter?.selected?.[0]?.value;
        const passTerminalId = passTerminalFilter?.selected?.[0]?.value;
        const failTerminalId = failTerminalFilter?.selected?.[0]?.value;
        const timeoutElementSysname = timeoutMinutesFilter?.selected?.[0]?.elementSysname;
        const timeoutSelectedValue = timeoutMinutesFilter?.selected?.[0]?.value;
        const timeoutSecondsValue = timeoutElementSysname
            ? TIMEOUT_BY_SYSNAME[timeoutElementSysname] ?? timeoutSelectedValue ?? TIMEOUT_BY_SYSNAME[DEFAULT_TIMEOUT_SYSNAME]
            : timeoutSelectedValue ?? TIMEOUT_BY_SYSNAME[DEFAULT_TIMEOUT_SYSNAME];
        const timeoutSecondsRaw = Number(timeoutSecondsValue);
        const timeoutSecondsFromLegacyMinutes = !timeoutElementSysname
            && Number.isFinite(timeoutSecondsRaw)
            && timeoutSecondsRaw > 0
            && timeoutSecondsRaw <= 60
            ? timeoutSecondsRaw * 60
            : timeoutSecondsRaw;
        const timeoutSeconds = Number.isFinite(timeoutSecondsFromLegacyMinutes) && timeoutSecondsFromLegacyMinutes > 0
            ? timeoutSecondsFromLegacyMinutes
            : 900;
        const timeoutMinutes = Number.isFinite(timeoutSeconds) && timeoutSeconds > 0
            ? Math.max(1, Math.ceil(timeoutSeconds / 60))
            : 15;
        const selectedDateValue = dateFilter?.selected?.[0]?.value;
        const [startDate, endDate] = selectedDateValue?.includes(';')
            ? selectedDateValue.split(';').map(v => v.trim())
            : [selectedDateValue ?? getTodayDate(), selectedDateValue ?? getTodayDate()];

        if (!entryTerminalId || !passTerminalId || !failTerminalId) {
            warn(translate.byKey(TranslationKeys.pleaseSelectReader));
            return { rows: [], pagination: { maxPageSize: 0, pageNumber: 0, totalCount: 0, totalPages: 0 } };
        }

        const pageNumber = listParameters?.goToPage ?? 1;
        const pageSize = listParameters?.pageSize ?? 20;

        let url = `custom/reportSequentialEvent?filter[entryTerminalId]=${entryTerminalId}&filter[passTerminalId]=${passTerminalId}&filter[failTerminalId]=${failTerminalId}&filter[timeoutSeconds]=${timeoutSeconds}&filter[timeoutMinutes]=${timeoutMinutes}&filter[startDate]=${startDate}&filter[endDate]=${endDate}&page[number]=${pageNumber}&page[size]=${pageSize}`;
        url = appendCustomReportFilters(url, listParameters, ['entryTerminalId', 'passTerminalId', 'failTerminalId', 'timeoutMinutes', 'dateOnly']);
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            const records: SequentialEventReport[] = [];
            const items: any[] = Array.isArray(json.data) ? json.data : [];

            items.forEach((item) => {
                const attr = item.attributes ?? item;
                const resultValue = attr["result"] ?? "";
                const report: SequentialEventReport = {
                    id: item.id,
                    personName: attr["personName"] ?? "",
                    tagCode: attr["tagCode"] ?? "",
                    entryTime: attr["entryTime"] ?? "",
                    entryReader: attr["entryReader"] ?? "",
                    entryLocation: attr["entryLocation"] ?? "",
                    siteName: attr["siteName"] ?? "",
                    result: resultValue === "PASS"
                        ? "Pass"
                        : resultValue === "FAIL"
                            ? "Fail"
                            : resultValue === "INCOMPLETE"
                                ? "Incomplete"
                                : resultValue,
                    outcomeTime: attr["outcomeTime"] ?? "",
                    outcomeReader: attr["outcomeReader"] ?? "",
                    testDurationSecs: attr["testDurationSecs"] ?? 0
                };
                records.push(report);
            });

            return resolve({ rows: records, pagination });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const getOperationalComplianceReport = async (translate: Translator, listParameters?: ListQuery): Promise<OperationalComplianceReportWrapper> => {
    try {
        const pageNumber = listParameters?.goToPage ?? 1;
        const pageSize = listParameters?.pageSize ?? 20;
        let url = `custom/reportOperationalCompliance?page[number]=${pageNumber}&page[size]=${pageSize}`;
        url = appendCustomReportFilters(url, listParameters, []);

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            const groupedRows: TableRowGroup<any>[] = [];
            const records: any[] = [];
            const items: any[] = Array.isArray(json.data) ? json.data : [];

            items.forEach((item) => {
                const attr = item.attributes ?? item;
                const parentRow: OperationalComplianceReport = {
                    id: item.id,
                    rowType: "parent",
                    masterId: attr["masterId"],
                    title: attr["mastTitle"],
                    displayName: attr["mstDisplayname"],
                    firstName: attr["mstFirstname"],
                    middleName: attr["mstMiddlename"],
                    lastName: attr["mstLastname"],
                    uid: attr["mstIdnumber"],
                    enableCredentials: attr["mstCurrent"],
                    enableCredentialLabel: attr.mstCurrent && attr.mstCurrent === 1 ? translate.byKey(TranslationKeys.yes) : translate.byKey(TranslationKeys.no),
                    companyId: attr["companyId"],
                    company: attr["compName"],
                    departmentId: attr["departmentId"],
                    department: attr["deptName"],
                    siteId: attr["siteId"],
                    site: attr.site?.["siteName"] ?? "",
                    profileId: attr["profileId"],
                    category: attr["profileName"],
                    conditionCount: attr["conditionCount"],
                    closestExpiryDays: attr["closestExpiryDays"],
                    overallStatus: attr["overallStatus"]
                };

                const complianceDetails: any[] = Array.isArray(attr["complianceDetails"]) ? attr["complianceDetails"] : [];
                const childRows = complianceDetails.map((complianceDetail: any, index: number) => ({
                    id: `${item.id}-condition-${index}`,
                    rowType: "child",
                    masterExpiryId: complianceDetail["masterExpiryId"],
                    expiryReasonId: complianceDetail["expiryReasonId"],
                    expiryReason: complianceDetail["erName"],
                    startDate: complianceDetail["meStartDateTime"],
                    endDate: formatDateOnly(complianceDetail["meEndDateTime"]),
                    daysRemaining: complianceDetail["daysRemaining"],
                    status: complianceDetail["status"]
                }));

                groupedRows.push({
                    id: item.id,
                    parentRow,
                    childRows,
                    childCount: childRows.length,
                    initiallyCollapsed: true
                });

                records.push(parentRow, ...childRows);
            });

            return resolve({ rows: records, groupedRows, pagination });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};